import { useNavigate } from 'react-router-dom';
import { ErrorState } from '../../components/States.js';
import { useErrorCopy } from '../../lib/errorCopy.js';
import { haptics } from '../../services/telegram.js';
import { useMine } from './useMining.js';
import { FitText } from '../../components/FitText.js';
import { useT, type CatalogKey } from '../../i18n/index.js';

/**
 * Home — the cover of PXC, as one painted picture.
 *
 * Home is NOT the mine. It is the cover: what you have, what it is earning, the
 * way in, and four shortcuts. The shaft itself is a full-screen experience one
 * tap away at /mining.
 *
 * The screen is a single render — styles/home-artwork.css, cut by
 * tools/slice_home_screen.py — so this file draws no panel, no bar, no plate and
 * no card. It sets four strings and a rate onto the spaces the master left, and
 * puts a hit area over the gold plate and each of the four cards.
 *
 * What the old composed Home carried and this one does not: the mining status
 * grid, the three figures (mined today, ready to claim, locked) and the recent
 * activity list. The design leaves them no room. Nothing is stranded by that —
 * the claim is an action of the mine and lives at /mining, and the activity list
 * is still reached from Wallet and from Profile.
 */
export function HomePage() {
  const { t } = useT();
  const describeError = useErrorCopy();
  const navigate = useNavigate();
  const { data: mine, isPending, isError, error, refetch } = useMine();

  if (isError || (!isPending && !mine)) {
    const described = describeError(error);
    return (
      <div className="page">
        <ErrorState
          title={described.title}
          {...(described.description ? { description: described.description } : {})}
          onRetry={() => void refetch()}
          retryLabel={t('common.retry')}
        />
      </div>
    );
  }

  const go = (path: string) => () => {
    haptics.select();
    navigate(path);
  };

  return (
    /*
      The picture is painted whether or not the figures have landed; only the
      lettering waits, so the screen never assembles itself in front of anyone.
    */
    <div className={isPending ? 'home-art home-art--loading' : 'home-art'} aria-busy={isPending || undefined}>
      <section className="hm2">
        {/* ---- the balance panel ---------------------------------------- */}
        <span className="hm-at hm2__balance-label">{t('home.availableBalance')}</span>
        <span className="hm-at-l hm2__balance-value" style={figureFit(mine?.wallet.balance ?? '0.00')}>
          {mine?.wallet.balance ?? '0.00'}
        </span>

        {/* ---- the rate bar ----------------------------------------------
            CRAFTGEM, not PXC: the panel above has CRAFTGEM painted into the
            plate as the balance's unit, and one screen naming the same token
            two ways reads as a bug. */}
        <span className="hm-at-l hm2__rate">
          <b>{mine?.summary.currentRate ?? '0'}</b> {t('home.rateUnit')}
        </span>

        {/* ---- the gold plate -------------------------------------------- */}
        <button type="button" className="hm2__cta" onClick={go('/mining')} aria-label={t('home.enterMine')} />
        <FitText className="hm-at-l hm2__cta-label">{t('home.enterMine')}</FitText>

        {/* ---- the daily combo -------------------------------------------
            Its badge floats on the hero; it opens the combo board. */}
        <button type="button" className="hm2__combo" aria-label={t('home.dailyCombo')} onClick={go('/combo')} />

        {/* ---- the roulette ----------------------------------------------
            Its badge floats under the combo's; it opens the prize wheel. */}
        <button type="button" className="hm2__roulette" aria-label={t('home.roulette')} onClick={go('/roulette')} />

        {/* ---- the four shortcuts ----------------------------------------
            The chevrons either side of the row are painted into the master; the
            dots under it are drawn. Either way the row is four shortcuts, not a
            carousel: nothing here moves them. */}
        {CARDS.map((card) => (
          <button
            key={card.to}
            type="button"
            className={`hm2__card ${card.column}`}
            onClick={go(card.to)}
            aria-label={t(card.titleKey)}
          />
        ))}
        {/* Title and note are fitted to the card (FitText): a language whose
            words run wider than the English would otherwise spill past the
            frame. */}
        {CARDS.map((card) => (
          <FitText key={card.to} className={`hm-at hm2__card-title ${card.column} ${card.tint}`}>
            {t(card.titleKey)}
          </FitText>
        ))}
        {CARDS.map((card) => (
          <FitText
            key={card.to}
            className={`hm-at-top hm2__card-note ${card.column}`}
            watch={`${t(card.noteKeys[0])}\n${t(card.noteKeys[1])}`}
          >
            {t(card.noteKeys[0])}
            <br />
            {t(card.noteKeys[1])}
          </FitText>
        ))}
      </section>

      {/* The master painted these on sixty-two rows of flat black, which read as
          a bar laid over the app's own rock. The plate is cut above that band
          now and the five circles are drawn back onto the rock instead. */}
      <div className="hm2-dots" aria-hidden>
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

/**
 * Keeps the balance inside the space the picture leaves it.
 *
 * The panel has its unit painted on, a gem and the word CRAFTGEM starting at
 * x 451 of the master's 854. The figure is set from x 211, so it has 230 pixels
 * to live in before it would collide — and at the size the design draws it,
 * 10.073cqw with 0.181em of tracking, that is four characters. "5.00" is four
 * characters, which is what the reference shows; "1,250.00" is eight, and the
 * server sends whichever the balance is.
 *
 * So anything longer than the reference's own figure is sized down until it
 * fits, and the wide tracking the design gives four characters is pulled back
 * to 0.08em, which buys most of the room without touching the letterforms.
 *
 * The two constants are Oswald 700's own metrics, read out of the font by
 * tools/wallet-preview/solve_type.py: a digit advances 0.5207em and is the
 * widest glyph formatMpxc can emit, so an all-digit string of a given length is
 * the worst case for that length. Change --font-display and they need
 * re-deriving, along with everything in home-artwork.css.
 */
const DIGIT_ADVANCE_EM = 0.5207;
const FIGURE_RUN_PX = 230;
const FIGURE_REFERENCE_PX = 86.02;   /* 10.073cqw of the master's 854 */

function figureFit(figure: string): { fontSize: string; letterSpacing: string } | undefined {
  const count = figure.length;
  if (count <= 4) return undefined;   // the reference's own case: leave it to the stylesheet
  const tracking = 0.08;
  const px = Math.min(
    FIGURE_REFERENCE_PX,
    FIGURE_RUN_PX / (DIGIT_ADVANCE_EM * count + tracking * (count - 1)),
  );
  return { fontSize: `${((px / 854) * 100).toFixed(3)}cqw`, letterSpacing: `${tracking}em` };
}

/**
 * The four shortcuts, in the order the master paints their scenes: the lone
 * miner, the two shaking hands, the chest, the clipboard.
 *
 * Both lines of every note are written out rather than left to wrap. At a
 * phone's width no box width reproduces the reference's four breaks at once,
 * and rounding moves a word between lines; a written break holds at any width.
 */
const CARDS = [
  {
    to: '/mining',
    column: 'hm2__col-1',
    tint: 'hm2__card-title--mining',
    titleKey: 'home.card.mining',
    noteKeys: ['home.card.miningNote1', 'home.card.miningNote2'],
  },
  {
    to: '/invite',
    column: 'hm2__col-2',
    tint: 'hm2__card-title--invite',
    titleKey: 'home.card.invite',
    noteKeys: ['home.card.inviteNote1', 'home.card.inviteNote2'],
  },
  {
    to: '/wallet',
    column: 'hm2__col-3',
    tint: 'hm2__card-title--wallet',
    titleKey: 'home.card.wallet',
    noteKeys: ['home.card.walletNote1', 'home.card.walletNote2'],
  },
  {
    to: '/tasks',
    column: 'hm2__col-4',
    tint: 'hm2__card-title--tasks',
    titleKey: 'home.card.tasks',
    noteKeys: ['home.card.tasksNote1', 'home.card.tasksNote2'],
  },
] as const satisfies readonly {
  to: string;
  column: string;
  tint: string;
  titleKey: CatalogKey;
  noteKeys: readonly [CatalogKey, CatalogKey];
}[];
